import React, { useEffect, useMemo, useState } from 'react';
import {
    playersAPI,
    playertoolsAPI,
    projectsAPI,
    positionsAPI,
    teamsAPI,
} from '../../services/api';
import {
    buildImportPreview,
    parsePlayerList,
    resolvePositionId,
} from '../../utils/playerListParser';

const STATUS = {
    new: { label: 'Neu', cls: 'badge-info' },
    existing: { label: 'Vorhanden', cls: 'badge-success' },
    assigned: { label: 'Im Kader', cls: 'badge-warning' },
    error: { label: 'Fehler', cls: 'badge-error' },
    done: { label: 'Importiert', cls: 'badge-success' },
};

export default function PlayerQuickImporter() {
    const [projects, setProjects] = useState([]);
    const [teams, setTeams] = useState([]);
    const [positions, setPositions] = useState([]);
    const [existingPlayers, setExistingPlayers] = useState([]);
    const [existingAssignments, setExistingAssignments] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [teamId, setTeamId] = useState('');
    const [rawText, setRawText] = useState('');
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [results, setResults] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [projRes, posRes, playersRes] = await Promise.all([
                    projectsAPI.getAll(),
                    positionsAPI.getAll(),
                    playersAPI.getAll({ size: 5000 }),
                ]);
                setProjects(projRes.data || []);
                setPositions(posRes.data || []);
                const playersData = playersRes.data?.players || playersRes.data || [];
                setExistingPlayers(Array.isArray(playersData) ? playersData : []);
            } catch (err) {
                setError(err.response?.data?.message || 'Daten konnten nicht geladen werden');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (!projectId) {
            setTeams([]);
            setTeamId('');
            setExistingAssignments([]);
            return;
        }
        teamsAPI.getByProject(projectId)
            .then((res) => setTeams(res.data || []))
            .catch(() => setTeams([]));
    }, [projectId]);

    useEffect(() => {
        if (!projectId || !teamId) {
            setExistingAssignments([]);
            return;
        }
        playertoolsAPI.getAll({ pid: projectId, tid: teamId, size: 5000 })
            .then((res) => {
                const data = res.data?.playertools || res.data || [];
                setExistingAssignments(Array.isArray(data) ? data : []);
            })
            .catch(() => setExistingAssignments([]));
    }, [projectId, teamId]);

    const positionNames = useMemo(
        () => positions.map((p) => p.name).filter(Boolean),
        [positions]
    );

    const parsed = useMemo(
        () => parsePlayerList(rawText, positionNames),
        [rawText, positionNames]
    );

    const preview = useMemo(
        () => buildImportPreview(parsed, existingPlayers, existingAssignments, positions),
        [parsed, existingPlayers, existingAssignments, positions]
    );

    const importable = preview.filter((row) => row.status !== 'assigned');
    const canImport = projectId && teamId && importable.length > 0 && !importing;

    const handleImport = async () => {
        if (!canImport) return;
        setImporting(true);
        setError('');
        const outcome = [];

        for (const row of preview) {
            if (row.status === 'assigned') {
                outcome.push({ ...row, importStatus: 'skipped', importMessage: row.message });
                continue;
            }

            try {
                let plid = row.matchedPlayer?._id;

                if (!plid) {
                    const created = await playersAPI.create({
                        surname: row.surname,
                        prename: row.prename,
                        posid: row.posid || null,
                        state: true,
                    });
                    plid = created.data._id;
                    setExistingPlayers((prev) => [...prev, created.data]);
                }

                await playertoolsAPI.create({
                    plid,
                    pid: projectId,
                    tid: teamId,
                    num: row.num ?? 0,
                    published: true,
                });

                outcome.push({
                    ...row,
                    importStatus: 'done',
                    importMessage: row.status === 'new' ? 'Spieler + Kader angelegt' : 'Kader-Eintrag angelegt',
                });
            } catch (err) {
                const msg = err.response?.data?.message || err.message;
                outcome.push({ ...row, importStatus: 'error', importMessage: msg });
            }
        }

        setResults(outcome);
        setImporting(false);

        if (projectId && teamId) {
            playertoolsAPI.getAll({ pid: projectId, tid: teamId, size: 5000 })
                .then((res) => {
                    const data = res.data?.playertools || res.data || [];
                    setExistingAssignments(Array.isArray(data) ? data : []);
                })
                .catch(() => {});
        }
    };

    const displayRows = results.length ? results : preview;

    if (loading) {
        return <div className="flex justify-center py-12"><span className="loading loading-spinner loading-lg" /></div>;
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Spieler Quick-Import</h1>
                <p className="text-sm text-slate-500 mt-1">
                    Spielerliste einfügen — vorhandene Spieler werden erkannt, neue automatisch angelegt.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="form-control">
                    <label className="label"><span className="label-text font-medium">Projekt</span></label>
                    <select
                        className="select select-bordered w-full"
                        value={projectId}
                        onChange={(e) => { setProjectId(e.target.value); setTeamId(''); setResults([]); }}
                    >
                        <option value="">Projekt wählen…</option>
                        {projects.map((p) => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                    </select>
                </div>
                <div className="form-control">
                    <label className="label"><span className="label-text font-medium">Verein</span></label>
                    <select
                        className="select select-bordered w-full"
                        value={teamId}
                        onChange={(e) => { setTeamId(e.target.value); setResults([]); }}
                        disabled={!projectId}
                    >
                        <option value="">Verein wählen…</option>
                        {teams.map((t) => (
                            <option key={t._id} value={t._id}>{t.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="form-control">
                <label className="label">
                    <span className="label-text font-medium">Spielerliste einfügen</span>
                    <span className="label-text-alt text-slate-400">Letzte Zahl = Trikotnummer</span>
                </label>
                <textarea
                    className="textarea textarea-bordered font-mono text-xs h-64"
                    placeholder="Liste aus dem Vereinsportal kopieren und hier einfügen…"
                    value={rawText}
                    onChange={(e) => { setRawText(e.target.value); setResults([]); }}
                />
            </div>

            {parsed.length > 0 && (
                <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                    <span><strong>{parsed.length}</strong> Spieler erkannt</span>
                    <span>{preview.filter((r) => r.status === 'new').length} neu</span>
                    <span>{preview.filter((r) => r.status === 'existing').length} vorhanden</span>
                    <span>{preview.filter((r) => r.status === 'assigned').length} bereits im Kader</span>
                </div>
            )}

            {error && <div className="alert alert-error text-sm">{error}</div>}

            {displayRows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="table table-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th>Name</th>
                                <th>Nr.</th>
                                <th>Position</th>
                                <th>Aktion</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayRows.map((row, idx) => {
                                const st = row.importStatus
                                    ? STATUS[row.importStatus] || STATUS.error
                                    : STATUS[row.status] || STATUS.error;
                                const statusText = row.importMessage || row.message;
                                return (
                                    <tr key={`${row.fullName}-${idx}`}>
                                        <td className="font-medium">{row.fullName}</td>
                                        <td>{row.num ?? '–'}</td>
                                        <td>{row.position || '–'}</td>
                                        <td className="text-xs text-slate-500">{row.message}</td>
                                        <td>
                                            <span className={`badge badge-sm ${st.cls}`}>{st.label}</span>
                                            {row.importStatus === 'error' && (
                                                <span className="block text-xs text-error mt-1">{statusText}</span>
                                            )}
                                            {row.importStatus === 'done' && (
                                                <span className="block text-xs text-success mt-1">{statusText}</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="flex gap-3">
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!canImport}
                    onClick={handleImport}
                >
                    {importing ? 'Import läuft…' : `${importable.length} Spieler importieren`}
                </button>
                {results.length > 0 && (
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => { setResults([]); setRawText(''); }}
                    >
                        Zurücksetzen
                    </button>
                )}
            </div>
        </div>
    );
}
